import Api from 'api';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import insuranceCarrierTypes from 'constants/insuranceCarrierTypes';
import { RI_GENERIC_CID, MONTHLY_FREQUENCY } from 'constants/common';
import { format } from 'date-fns';
import { setGlobalLoading, unsetGlobalLoading } from './globalLoading';
import { removeError, addError } from './errors';

// Action types
const REQUEST_ID_THEFT_QUOTE = 'ri/idTheft/REQUEST_ID_THEFT_QUOTE';
const RECEIVE_ID_THEFT_QUOTE_SUCCESS = 'ri/idTheft/RECEIVE_ID_THEFT_QUOTE_SUCCESS';
const RECEIVE_ID_THEFT_QUOTE_ERROR = 'ri/idTheft/RECEIVE_ID_THEFT_QUOTE_ERROR';
const DETERMINE_ACTIVE_PLAN_GROUP = 'ri/idTheft/DETERMINE_ACTIVE_PLAN_GROUP';

const REQUEST_CREATE_ID_THEFT_POLICY = 'ri/idTheft/REQUEST_CREATE_ID_THEFT_POLICY';
const RECEIVE_CREATE_ID_THEFT_POLICY_SUCCESS = 'ri/idTheft/RECEIVE_CREATE_ID_THEFT_POLICY_SUCCESS';
const RECEIVE_CREATE_ID_THEFT_POLICY_ERROR = 'ri/idTheft/RECEIVE_CREATE_ID_THEFT_POLICY_ERROR';

const REQUEST_ENROLL_ID_THEFT_POLICY = 'ri/idTheft/REQUEST_ENROLL_ID_THEFT_POLICY';
const RECEIVE_ENROLL_ID_THEFT_POLICY_SUCCESS = 'ri/idTheft/RECEIVE_ENROLL_ID_THEFT_POLICY_SUCCESS';
const RECEIVE_ENROLL_ID_THEFT_POLICY_ERROR = 'ri/idTheft/RECEIVE_ENROLL_ID_THEFT_POLICY_ERROR';

const UPDATE_ID_THEFT_PAYMENT_INFO = 'ri/idTheft/UPDATE_ID_THEFT_PAYMENT_INFO';
const SET_ACTIVE_PLAN_GROUP = 'ri/idTheft/SET_ACTIVE_PLAN_GROUP';
const SET_AUTO_SELECTED_PLAN = 'ri/idTheft/SET_AUTO_SELECTED_PLAN';
const SET_IS_TEST = 'ri/idTheft/SET_IS_TEST';

// initial state
const initialState = {
  quote: {
    loading: false,
    data: null,
    error: null,
  },
  activePlanGroup: 0,
  autoSelectedPlan: null,
  isTest: null,
  policyCreation: {
    loading: false,
    success: false,
    error: null,
    policyData: null,
  },
  policyEnrollment: {
    loading: false,
    success: false,
    error: null,
    enrollmentData: null,
  },
  paymentInfo: {
    paymentType: 'credit',
    paymentTypeId: '',
    ccNameOnCard: '',
    ccCardNumberMasked: '',
    authToken: '',
    secureReferenceNumber: '',
    ccExpDateMonth: '',
    ccExpDateYear: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    sameAsPhysical: true,
    agreeToTerms: false,
  },
};

// reducer
// eslint-disable-next-line default-param-last
export default function idTheftReducer(state = initialState, action) {
  switch (action.type) {
    case REQUEST_ID_THEFT_QUOTE:
      return {
        ...state,
        quote: {
          ...state.quote,
          loading: true,
          error: null,
        },
      };
    case RECEIVE_ID_THEFT_QUOTE_SUCCESS:
      return {
        ...state,
        quote: {
          loading: false,
          data: action.payload,
          error: null,
        },
      };
    case RECEIVE_ID_THEFT_QUOTE_ERROR:
      return {
        ...state,
        quote: {
          loading: false,
          data: null,
          error: action.payload,
        },
      };
    case REQUEST_CREATE_ID_THEFT_POLICY:
      return {
        ...state,
        policyCreation: {
          ...state.policyCreation,
          loading: true,
          error: null,
        },
      };
    case RECEIVE_CREATE_ID_THEFT_POLICY_SUCCESS:
      return {
        ...state,
        policyCreation: {
          loading: false,
          success: true,
          error: null,
          policyData: action.payload,
        },
      };
    case RECEIVE_CREATE_ID_THEFT_POLICY_ERROR:
      return {
        ...state,
        policyCreation: {
          loading: false,
          success: false,
          error: action.payload,
          policyData: null,
        },
      };
    case REQUEST_ENROLL_ID_THEFT_POLICY:
      return {
        ...state,
        policyEnrollment: {
          ...state.policyEnrollment,
          loading: true,
          error: null,
        },
      };
    case RECEIVE_ENROLL_ID_THEFT_POLICY_SUCCESS:
      return {
        ...state,
        policyEnrollment: {
          loading: false,
          success: true,
          error: null,
          enrollmentData: action.payload,
        },
      };
    case RECEIVE_ENROLL_ID_THEFT_POLICY_ERROR:
      return {
        ...state,
        policyEnrollment: {
          loading: false,
          success: false,
          error: action.payload,
          enrollmentData: null,
        },
      };
    case UPDATE_ID_THEFT_PAYMENT_INFO:
      return {
        ...state,
        paymentInfo: {
          ...state.paymentInfo,
          ...action.payload,
        },
      };
    case SET_ACTIVE_PLAN_GROUP:
      return {
        ...state,
        activePlanGroup: action.payload,
      };
    case SET_AUTO_SELECTED_PLAN:
      return {
        ...state,
        autoSelectedPlan: action.payload,
      };
    case SET_IS_TEST:
      return {
        ...state,
        isTest: action.payload,
      };
    default:
      return state;
  }
}

// action creators
export const requestIdTheftQuote = () => ({
  type: REQUEST_ID_THEFT_QUOTE,
});

export const receiveIdTheftQuote = (quoteData) => ({
  type: RECEIVE_ID_THEFT_QUOTE_SUCCESS,
  payload: quoteData,
});

export const idTheftQuoteError = (error) => ({
  type: RECEIVE_ID_THEFT_QUOTE_ERROR,
  payload: error,
});

export const requestCreateIdTheftPolicy = (policyData) => ({
  type: REQUEST_CREATE_ID_THEFT_POLICY,
  payload: policyData,
});

export const receiveCreateIdTheftPolicySuccess = (policyData) => ({
  type: RECEIVE_CREATE_ID_THEFT_POLICY_SUCCESS,
  payload: policyData,
});

export const receiveCreateIdTheftPolicyError = (error) => ({
  type: RECEIVE_CREATE_ID_THEFT_POLICY_ERROR,
  payload: error,
});

export const requestEnrollIdTheftPolicy = (policyData) => ({
  type: REQUEST_ENROLL_ID_THEFT_POLICY,
  payload: policyData,
});

export const receiveEnrollIdTheftPolicySuccess = (policyData) => ({
  type: RECEIVE_ENROLL_ID_THEFT_POLICY_SUCCESS,
  payload: policyData,
});

export const receiveEnrollIdTheftPolicyError = (error) => ({
  type: RECEIVE_ENROLL_ID_THEFT_POLICY_ERROR,
  payload: error,
});

export const updateIdTheftPaymentInfo = (paymentInfo) => ({
  type: UPDATE_ID_THEFT_PAYMENT_INFO,
  payload: paymentInfo,
});

export const setActivePlanGroup = (groupIndex) => ({
  type: SET_ACTIVE_PLAN_GROUP,
  payload: groupIndex,
});

export const setAutoSelectedPlan = (planDetails) => ({
  type: SET_AUTO_SELECTED_PLAN,
  payload: planDetails,
});

export const determineActivePlanGroup = (options = {}) => ({
  type: DETERMINE_ACTIVE_PLAN_GROUP,
  payload: options,
});

export const setIsTest = (isTest) => ({
  type: SET_IS_TEST,
  payload: isTest,
});

function* getIdTheftQuote() {
  try {
    yield put(setGlobalLoading('Loading identity theft protection plans...'));
    yield put(removeError('idTheftQuoteError'));

    const payload = {
      insuranceCarrierId: insuranceCarrierTypes.ARRAY,
    };

    const response = yield call(Api.getIdTheftQuote, payload);

    if (response.responseCode === 200 && response.result) {
      yield put(receiveIdTheftQuote(response.result));
    } else {
      // API returned non-200 or no result
      const errorMessage = response.message || 'Failed to load identity theft plans';
      yield put(idTheftQuoteError(errorMessage));
    }
  } catch (err) {
    const errorMessage = err.message || 'Unable to load plans at this time. Please try again later.';
    yield put(idTheftQuoteError(errorMessage));
  } finally {
    yield put(unsetGlobalLoading());
  }
}

function* determineActivePlanGroupSaga(action) {
  try {
    const { planId, amplitudeVariant } = action.payload;
    const state = yield select();
    const quoteData = state.idTheft?.quote?.data;

    if (!quoteData?.groups) {
      yield put(setActivePlanGroup(0));
      return;
    }

    if (planId) {
      const groups = quoteData.groups || [];
      for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
        const group = groups[groupIndex];
        const plans = group.plans || [];
        const matchingPlan = plans.find((plan) => plan.id?.toString() === planId.toString());
        if (matchingPlan) {
          yield put(setActivePlanGroup(groupIndex));

          const selectedPlanDetails = {
            planId: matchingPlan.id,
            premiumAmount: matchingPlan.basePremium,
            planType: matchingPlan.planType || matchingPlan.planName,
            planTypeId: matchingPlan.planTypeId,
            policyFrequencyId: MONTHLY_FREQUENCY,
            insuranceCarrierId: insuranceCarrierTypes.ARRAY,
          };
          yield put(setAutoSelectedPlan(selectedPlanDetails));
          return;
        }
      }
    }

    if (amplitudeVariant?.value !== undefined) {
      let groupIndex = 0;
      if (amplitudeVariant.value === 'group1') {
        groupIndex = 0;
      } else if (amplitudeVariant.value === 'group2') {
        groupIndex = 1;
      } else if (amplitudeVariant.value === 'group3') {
        groupIndex = 2;
      }
      yield put(setActivePlanGroup(groupIndex));
      return;
    }

    yield put(setActivePlanGroup(0));
  } catch (err) {
    yield put(setActivePlanGroup(0));
  }
}

function* createIdTheftPolicy(action) {
  try {
    yield put(setGlobalLoading('Processing...'));
    yield put(removeError('createIdTheftPolicyError'));

    const { payload } = action;
    const {
      planDetails,
      customerDetails,
      insurancePolicyId,
      userId: payloadUserId,
      employeeId: payloadEmployeeId,
      entityId: payloadEntityId,
      leadSourceTypeId = 1,
    } = payload;

    // Get userId and employeeId from redux state if not provided in payload
    const state = yield select();
    const userId = payloadUserId || state.getQuote?.user || state.user?.id;
    const employeeId = payloadEmployeeId || state.getQuote?.employee?.id || null;
    const entityId = payloadEntityId || state.user?.entityId || null;
    const isTest = state.idTheft?.isTest;

    const apiPayload = {
      userId,
      employeeId,
      entityId,
      leadSourceTypeId,
      insuranceCarrierId: planDetails.insuranceCarrierId,
      policyFrequencyId: planDetails.policyFrequencyId,
      planId: planDetails.planId,
      effectiveDate: format(new Date(), 'MM/DD/YYYY'),
      ...(isTest && { isTest: true }),
      customerDetails: {
        emailAddress: customerDetails.email,
        nameFirst: customerDetails.firstName,
        nameLast: customerDetails.lastName,
        streetAddress: customerDetails.streetAddress,
        city: customerDetails.city,
        stateCode: customerDetails.state,
        postalCode: customerDetails.zipCode,
        phoneNumber: customerDetails.phone,
      },
    };

    if (insurancePolicyId) {
      apiPayload.insurancePolicyId = insurancePolicyId;
      yield put(setGlobalLoading('Processing...'));
    }

    const response = yield call(Api.createIdTheftPolicy, apiPayload);

    if (response.responseCode === 200) {
      yield put(receiveCreateIdTheftPolicySuccess(response.result));
      if (payload.onSuccess) {
        payload.onSuccess(response.result);
        // Keep loading visible during page transition - will be unset by payment page
      }
    } else if (response.responseCode === 203) {
      yield put(receiveCreateIdTheftPolicyError('Session expired. Please log in again.'));
    } else {
      yield put(receiveCreateIdTheftPolicyError(response.message || 'Failed to create policy'));
      yield put(addError({ key: 'createIdTheftPolicyError', message: response.message }));
    }
  } catch (err) {
    yield put(receiveCreateIdTheftPolicyError(err.message || 'An unexpected error occurred'));
    yield put(addError({ key: 'createIdTheftPolicyError', message: err.toString() }));
    if (window.NREUM?.noticeError) window.NREUM.noticeError(err);
  } finally {
    // Only unset loading if we're not navigating to next page
    yield put(unsetGlobalLoading());
  }
}

function* enrollIdTheftPolicy(action) {
  try {
    const { payload } = action;
    const { planDetails, customerDetails, paymentDetails, policyData, existingAccountId } = payload;

    yield put(setGlobalLoading('Processing your payment...'));

    const { entityId, homebodyUserId, homebodyUuid, insurancePolicyId } = policyData || {};

    // Get userId and cid from redux state
    const state = yield select();
    const userId = state.getQuote?.user || state.user?.id;
    const cid = RI_GENERIC_CID;
    const employeeId = state.getQuote?.employee?.id || null;

    const apiPayload = {
      userId,
      employeeId,
      planId: planDetails.planId,
      cid,
      insurancePolicyId,
      insuranceCarrierId: planDetails.insuranceCarrierId,
      entityId,
      homebodyUserId,
      homebodyUuid,
      ...(existingAccountId && { existingAccountId }),
      customerDetails: {
        nameFirst: customerDetails.firstName,
        nameLast: customerDetails.lastName,
        emailAddress: customerDetails.email,
        phoneNumber: customerDetails.phone,
        password: '',
        streetAddress: customerDetails.streetAddress,
        city: customerDetails.city,
        stateCode: customerDetails.state,
        postalCode: customerDetails.zipCode,
        mailingStreetAddress: paymentDetails.sameAsPhysical
          ? customerDetails.streetAddress
          : paymentDetails.billingAddress,
        mailingState: paymentDetails.sameAsPhysical ? customerDetails.state : paymentDetails.billingState,
        mailingCity: paymentDetails.sameAsPhysical ? customerDetails.city : paymentDetails.billingCity,
        mailingPostalCode: paymentDetails.sameAsPhysical ? customerDetails.zipCode : paymentDetails.billingZip,
        ssnData: [],
      },
      paymentDetails: {
        isSubmitLater: false,
        paymentTypeId: paymentDetails.paymentTypeId,
        billingStreetLine: paymentDetails.sameAsPhysical
          ? customerDetails.streetAddress
          : paymentDetails.billingAddress,
        billingCity: paymentDetails.sameAsPhysical ? customerDetails.city : paymentDetails.billingCity,
        billingStateCode: paymentDetails.sameAsPhysical ? customerDetails.state : paymentDetails.billingState,
        billingPostalCode: paymentDetails.sameAsPhysical ? customerDetails.zipCode : paymentDetails.billingZip,
        billtoEmailAddress: customerDetails.email,
        billtoPhoneNumber: customerDetails.phone,
        billtoNameFirst: customerDetails.firstName,
        billtoNameLast: customerDetails.lastName,
        accountName: '',
        checkBankName: '',
        accountTypeId: '',
        routingNumber: '',
        accountNumber: '',
        confirmAccountNumber: '',
        nameOnCard: paymentDetails.cardholderName,
        ccNumber: paymentDetails.ccCardNumberMasked || '',
        expMonth: paymentDetails.expMonth || '',
        expYear: paymentDetails.expYear || '',
        authToken: paymentDetails.authToken || null,
      },
    };

    const response = yield call(Api.enrollIdTheftPolicy, apiPayload);

    if (response.responseCode === 200) {
      yield put(receiveEnrollIdTheftPolicySuccess(response.result));
      if (payload.onSuccess) {
        payload.onSuccess(response.result);
      }
    } else if (response.responseCode === 203) {
      yield put(receiveEnrollIdTheftPolicyError('Session expired. Please log in again.'));
    } else {
      yield put(receiveEnrollIdTheftPolicyError(response.message || 'Payment processing failed'));
      yield put(addError({ key: 'enrollIdTheftPolicyError', message: response.message }));
    }
  } catch (err) {
    yield put(receiveEnrollIdTheftPolicyError(err.message || 'An unexpected error occurred'));
    yield put(addError({ key: 'enrollIdTheftPolicyError', message: err.toString() }));
    if (window.NREUM?.noticeError) window.NREUM.noticeError(err);
  } finally {
    yield put(unsetGlobalLoading());
  }
}

function* watchGetIdTheftQuote() {
  yield takeLatest(REQUEST_ID_THEFT_QUOTE, getIdTheftQuote);
}

function* watchCreateIdTheftPolicy() {
  yield takeLatest(REQUEST_CREATE_ID_THEFT_POLICY, createIdTheftPolicy);
}

function* watchEnrollIdTheftPolicy() {
  yield takeLatest(REQUEST_ENROLL_ID_THEFT_POLICY, enrollIdTheftPolicy);
}

function* watchDetermineActivePlanGroup() {
  yield takeLatest(DETERMINE_ACTIVE_PLAN_GROUP, determineActivePlanGroupSaga);
}

export const sagas = [
  watchGetIdTheftQuote,
  watchCreateIdTheftPolicy,
  watchEnrollIdTheftPolicy,
  watchDetermineActivePlanGroup,
];
